/**
 * @fileoverview API calls for the class-wide Development Report (AR-545).
 *
 * The backend already aggregates the three score sources and annotates the
 * cached curriculum trees. Here we only defensively normalize the raw payload
 * into the recursive domain shape (`CurriculumReport`).
 */

import { ARC_MOSAICO_ENDPOINTS } from 'api/arcMosaico'
import { api } from 'helpers'
import type { CurriculumReport, ReportClass, ReportLv1, ReportNode } from '../types'

const FALLBACK_COLOR = 'var(--mantine-color-gray-5, #adb5bd)'

interface RawNode {
  id?: string
  name?: string
  originalCodes?: string
  assessmentAverage?: number | null
  children?: RawNode[]
}

interface RawLv1 extends RawNode {
  color?: string
  hasAssessment?: boolean
}

interface CurriculumApiResponse {
  standardsAssessed?: number
  skillsAssessed?: number
  content?: RawLv1[]
  skills?: RawLv1[]
}

function assertAuthenticated(response: { status: number }): void {
  if (response.status === 401) {
    window.location.href = '/error_permission'
    throw new Error('Unauthorized')
  }
}

function adaptNode(raw: RawNode | null | undefined): ReportNode {
  const rawChildren = Array.isArray(raw?.children) ? raw?.children ?? [] : []
  const children = rawChildren.map(adaptNode)
  const rawAverage = raw?.assessmentAverage
  const average = typeof rawAverage === 'number' ? rawAverage : null
  return {
    id: String(raw?.id ?? ''),
    name: String(raw?.name ?? ''),
    originalCodes: String(raw?.originalCodes ?? ''),
    average,
    children,
  }
}

function adaptLv1(raw: RawLv1 | null | undefined): ReportLv1 {
  const node = adaptNode(raw)
  return {
    id: node.id,
    name: node.name,
    color: raw?.color || FALLBACK_COLOR,
    hasAssessment: Boolean(raw?.hasAssessment),
    children: node.children,
  }
}

export async function fetchReportClasses(): Promise<ReportClass[]> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.newReport.groups)
  assertAuthenticated(response)
  const data = response.data as { groups?: Array<{ id?: string; name?: string }> }
  return (data.groups ?? [])
    .filter(group => group?.id)
    .map(group => ({ id: String(group.id), name: String(group.name ?? '') }))
}

export async function fetchCurriculumReport(groupId?: string): Promise<CurriculumReport> {
  const response = await api.get(ARC_MOSAICO_ENDPOINTS.newReport.curriculum(groupId))
  assertAuthenticated(response)
  const data = response.data as CurriculumApiResponse
  return {
    standardsAssessed: Number(data.standardsAssessed ?? 0),
    skillsAssessed: Number(data.skillsAssessed ?? 0),
    standards: Array.isArray(data.content) ? data.content.map(adaptLv1) : [],
    skills: Array.isArray(data.skills) ? data.skills.map(adaptLv1) : [],
  }
}
